/**
 * SVG Inline Image Extractor
 *
 * Walks an SVG document looking for `<image href="data:...">` payloads,
 * uploads each unique one to the cs-assets Storage bucket, inserts a row
 * into `cs_assets` under the "Recovered From Canvas" folder, and rewrites
 * the SVG markup to reference the Storage URL instead.
 *
 * Purpose: prevent base64 images inside SVGs from ballooning
 * `cs_projects.canvas_state` (which was crashing the editor and exhausting
 * the database connection pool).
 */

import { supabase } from './supabase';

const BUCKET = 'cs-assets';
const RECOVERY_FOLDER_NAME = 'Recovered From Canvas';

const DATA_URI_META_RE = /^data:([a-z+\-/.]+)(;base64)?,/i;

function mimeToExt(mime: string): string {
  switch (mime.toLowerCase()) {
    case 'image/png': return 'png';
    case 'image/jpeg':
    case 'image/jpg': return 'jpg';
    case 'image/gif': return 'gif';
    case 'image/webp': return 'webp';
    case 'image/svg+xml': return 'svg';
    default: return 'bin';
  }
}

async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

interface DecodedDataUri {
  blob: Blob;
  bytes: Uint8Array;
  mime: string;
}

function decodeDataUri(dataUri: string): DecodedDataUri | null {
  const meta = dataUri.match(DATA_URI_META_RE);
  if (!meta) return null;
  const mime = meta[1];
  const isBase64 = !!meta[2];
  const commaIdx = dataUri.indexOf(',');
  if (commaIdx < 0) return null;
  const payload = dataUri.slice(commaIdx + 1);

  let bytes: Uint8Array;
  try {
    if (isBase64) {
      const bin = atob(payload);
      bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    } else {
      bytes = new TextEncoder().encode(decodeURIComponent(payload));
    }
  } catch {
    return null;
  }
  return { blob: new Blob([bytes], { type: mime }), bytes, mime };
}

let cachedFolderId: string | null | undefined = undefined;
async function getOrCreateRecoveryFolderId(): Promise<string | null> {
  if (cachedFolderId !== undefined) return cachedFolderId;
  const { data: existing } = await supabase
    .from('cs_asset_folders')
    .select('id')
    .eq('name', RECOVERY_FOLDER_NAME)
    .maybeSingle();
  if (existing?.id) {
    cachedFolderId = existing.id;
    return cachedFolderId;
  }
  const { data: userData } = await supabase.auth.getUser();
  const { data: inserted } = await supabase
    .from('cs_asset_folders')
    .insert({
      name: RECOVERY_FOLDER_NAME,
      color: '#64748b',
      created_by: userData.user?.id || null,
    })
    .select('id')
    .maybeSingle();
  cachedFolderId = inserted?.id || null;
  return cachedFolderId;
}

async function uploadRecoveredImage(
  dataUri: string,
  sourceFileName: string
): Promise<string | null> {
  const decoded = decodeDataUri(dataUri);
  if (!decoded) return null;
  if (decoded.bytes.byteLength === 0) return null;

  const hash = await sha256Hex(decoded.bytes);
  const ext = mimeToExt(decoded.mime);
  const storagePath = `recovered-from-canvas/${hash}.${ext}`;

  const { data: existing } = await supabase
    .from('cs_assets')
    .select('id, storage_path')
    .eq('storage_path', storagePath)
    .maybeSingle();

  const { data: urlData } = supabase.storage.from(BUCKET).getPublicUrl(storagePath);
  const publicUrl = urlData.publicUrl;

  if (existing?.id) {
    return publicUrl;
  }

  const { error: uploadErr } = await supabase.storage
    .from(BUCKET)
    .upload(storagePath, decoded.blob, {
      contentType: decoded.mime,
      upsert: true,
    });
  if (uploadErr) {
    console.warn('[svgInlineImageExtractor] upload failed', uploadErr);
    return null;
  }

  const folderId = await getOrCreateRecoveryFolderId();
  const { data: userData } = await supabase.auth.getUser();
  const baseName = (sourceFileName || 'recovered').replace(/\.svg$/i, '');
  const shortHash = hash.slice(0, 8);

  await supabase.from('cs_assets').insert({
    name: `${baseName} (recovered ${shortHash})`,
    file_name: `${baseName}-${shortHash}.${ext}`,
    file_size: decoded.bytes.byteLength,
    original_file_size: decoded.bytes.byteLength,
    compressed: false,
    mime_type: decoded.mime,
    storage_path: storagePath,
    thumbnail_url: publicUrl,
    category: 'image',
    asset_type: 'image',
    tags: ['recovered-from-svg'],
    folder_id: folderId,
    is_favorite: false,
    usage_count: 0,
    created_by: userData.user?.id || null,
  });

  return publicUrl;
}

/**
 * Scans SVG text for `<image href="data:...">` nodes, uploads each unique
 * payload to Storage, and returns rewritten markup that references the
 * Storage URLs. If extraction fails for a given image (network error, etc.)
 * the `<image>` node is removed so the bloat never reaches the canvas.
 */
export async function extractInlineImagesFromSvg(
  svgText: string,
  sourceFileName: string
): Promise<string> {
  if (!/data:image\//i.test(svgText)) return svgText;

  let doc: Document;
  try {
    doc = new DOMParser().parseFromString(svgText, 'image/svg+xml');
  } catch {
    return svgText;
  }
  const imageNodes = Array.from(doc.querySelectorAll('image'));
  let mutated = false;
  for (const node of imageNodes) {
    const href =
      node.getAttribute('href') ||
      node.getAttribute('xlink:href') ||
      '';
    if (!href.startsWith('data:')) continue;

    const publicUrl = await uploadRecoveredImage(href, sourceFileName);
    if (publicUrl) {
      node.setAttribute('href', publicUrl);
      node.removeAttribute('xlink:href');
    } else {
      node.remove();
    }
    mutated = true;
  }
  if (!mutated) return svgText;
  return new XMLSerializer().serializeToString(doc);
}
